import { createRootRouteWithContext, createRoute, redirect } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { lazy } from 'react';

import { GenericErrorComponent } from '@/components/GenericErrorComponent';
import { Role, Settings } from '@/contexts/Settings.types';
import { singleUpdateSearchSchema, socialFeedSearchSchema } from '@/features/engagement/engagement.types';
import { straViewSearchSchema } from '@/features/stra/stra.types';
import { subscriptionSearchSchema } from '@/features/subscription/subscription.types';
import { createRouteContent } from '@/helpers/createRouteContent';
import { globalAdminRoutes } from '@/layouts/layout.constants';
import { getPages } from '@/layouts/navbar/navPages';
import RootLayout from '@/layouts/RootLayout';

import {
  collaborateEditSearchSchema,
  engagementSearchSchema,
  knowledgebaseSearchSchema,
  ourCalendarSearchSchema,
  ourJourneySearchSchema,
  progressUpdateSearchSchema,
  rootSearchSchema,
  songlineSearchSchema,
  strategiesSearchSchema
  // vacancySearchSchema,
} from './types/itemFilter';

// const VacancyPage = lazy(() => import('@/pages/vacancies/VacancyPage'));
// const VacancyApplicationsList = lazy(
//   () => import('@/pages/globalAdmin/vacancies/VacancyApplicationsList'),
// );

const Hangfire = lazy(() => import('@/pages/admin/Hangfire'));
const Blogs = lazy(() => import('@/pages/blogs/Blogs'));
const BlogsLanding = lazy(() => import('@/pages/blogs/BlogsLanding'));
const ContentBuilder = lazy(() => import('@/pages/contentBuilder/ContentBuilder'));
const CaseStudiesLanding = lazy(() => import('@/pages/caseStudies/CaseStudiesLanding'));
const CaseStudyPage = lazy(() => import('@/pages/caseStudies/CaseStudyPage'));
const KnowledgeBaseLanding = lazy(() => import('./components/knowledgeBase/KnowledgeBaseLanding'));
const KnowledgeBasePage = lazy(() => import('./components/knowledgeBase/KnowledgeBasePage'));
const SubscriptionEditPage = lazy(() => import('@/pages/globalAdmin/subscriptions/SubscriptionEditPage'));
const NotAuthorized = lazy(() => import('@/pages/NotAuthorized'));
const Security = lazy(() => import('@/pages/Security'));

const NotFound = lazy(() => import('@/pages/NotFound'));
const PrivacyPolicy = lazy(() => import('@/pages/PrivacyPolicy'));
const TermsConditions = lazy(() => import('@/pages/TermsConditions'));
const OurUseOfAI = lazy(() => import('@/pages/OurUseOfAI'));

// Lazy load the components
const HomePageOld2 = lazy(() => import('@/pages/home/HomePageOld2'));
const SocialFeedPage = lazy(() => import('@/pages/updates/SocialFeedPage'));
const SinglePostPage = lazy(() => import('@/pages/updates/SinglePostPage'));
const ReviewPublishPage = lazy(() => import('@/pages/updates/ReviewPublishPage'));
const HomePage = lazy(() => import('@/pages/HomePage'));
const StrategiesDashboardPage = lazy(() => import('@/pages/stra/StrategiesDashboardPage'));
const CreateNewStraPage = lazy(() => import('@/pages/stra/CreateNewStraPage'));
const StrategyViewPage = lazy(() => import('@/pages/stra/StrategyViewPage'));
//const Strategies = lazy(() => import('@/pages/strategies/Strategies'));
const CollaboratePage = lazy(() => import('@/pages/collaborate/CollaboratePage'));
const AppLookups = lazy(() => import('@/pages/globalAdmin/appLookups/AppLookups'));
const AppModules = lazy(() => import('@/pages/globalAdmin/appLookups/AppModules'));
const BusinessUnits = lazy(() => import('@/features/businessUnits/components/BusinessUnits'));
const Profile = lazy(() => import('@/pages/user/Profile'));
const ProgressUpdatePage = lazy(() => import('@/pages/collaborate/ProgressUpdatePage'));
const OurJourney = lazy(() => import('@/pages/reports/ourJourney/OurJourney'));
const Roles = lazy(() => import('@/pages/globalAdmin/roles/Roles'));
const UsersPage = lazy(() => import('@/pages/globalAdmin/users/UsersPage'));
const Subscriptions = lazy(() => import('@/pages/globalAdmin/subscriptions/Subscriptions'));
// const ExcelImport = lazy(() => import('@/pages/admin/ExcelImport'));
const Scheduler = lazy(() => import('@/pages/admin/Scheduler'));
const System = lazy(() => import('@/pages/admin/System'));
const Songline = lazy(() => import('@/pages/reports/songline/Songline'));
const PhotoAlbum = lazy(() => import('@/pages/reports/PhotoAlbum'));
const OurCalendar = lazy(() => import('@/pages/reports/ourCalendar/OurCalendar'));
const OurUpdates = lazy(() => import('@/features/progresses/components/ProgressUpdate_UserActivities'));
// const Vacancies = lazy(() => import('@/pages/vacancies/Vacancies'));

type RouterContext = {
  globalSettings: Settings;
};

export function checkRole(userRole: Role | undefined, allowedRoles: Role[] | undefined): boolean {
  if (!allowedRoles || allowedRoles.length === 0) return true;
  if (!userRole) return false;
  return allowedRoles.includes(userRole);
}

export function getAllowedRoles(path: string): Role[] | undefined {
  const pages = getPages([]);
  const pathParts = path.split('/').filter(Boolean);

  for (const page of pages) {
    if (typeof page === 'object') {
      const [key, value] = Object.entries(page)[0];

      const routePrefix = pathParts[0]?.replace('global_admin', 'Admin').toLowerCase();
      if (routePrefix === key.toLowerCase()) {
        if (pathParts.length === 1) {
          return value.roles;
        } else if (value.itemSets) {
          for (const itemSet of value.itemSets) {
            for (const item of itemSet.items) {
              if (
                typeof item === 'object' &&
                item.name?.replaceAll(' ', '_').toLowerCase() === pathParts[1].toLowerCase()
              ) {
                return itemSet.roles;
              }
            }
          }
        }
      }
    }
  }
  return undefined;
}

function createBeforeLoad(path: string) {
  return ({ context }: { context: RouterContext }) => {
    const allowedRoles = getAllowedRoles(path);
    if (!context.globalSettings) return;
    if (!checkRole(context.globalSettings.role, allowedRoles)) {
      throw redirect({ to: '/not-authorized' });
    }
  };
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootLayout
});

const homeRoute = createRoute({
  path: '/',
  getParentRoute: () => rootRoute,
  component: createRouteContent(HomePage, {
    title: 'Weavr',
    description:
      "Empower your organisation's reconciliation journey with weavr's innovative software and expert services. Track progress, manage strategies, and create meaningful impact for First Nations People. Discover our user-friendly tools and tailored support today."
  }),
  errorComponent: GenericErrorComponent,
  validateSearch: zodValidator(rootSearchSchema)
});
const termsConditionRoute = createRoute({
  path: '/terms',
  getParentRoute: () => rootRoute,
  component: createRouteContent(TermsConditions, {
    title: 'Terms and conditions',
    description: 'Weavr | Terms and conditions'
  }),
  errorComponent: GenericErrorComponent
});
const securityRoute = createRoute({
  path: '/security',
  getParentRoute: () => rootRoute,
  component: createRouteContent(Security, {
    title: 'Security',
    description: 'Weavr | Security'
  }),
  errorComponent: GenericErrorComponent
});
const privacyRoute = createRoute({
  path: '/privacy_policy',
  getParentRoute: () => rootRoute,
  component: createRouteContent(PrivacyPolicy, {
    title: 'Privacy policy',
    description: 'Weavr | Privacy policy'
  }),
  errorComponent: GenericErrorComponent
});
const ourUseOfAIRoute = createRoute({
  path: '/our_use_of_ai',
  getParentRoute: () => rootRoute,
  component: createRouteContent(OurUseOfAI, {
    title: 'Our Use of AI at Weavr',
    description: 'Weavr | Our Use of AI'
  }),
  errorComponent: GenericErrorComponent
});
const straRoute = createRoute({
  path: '/sstra',
  getParentRoute: () => rootRoute,
  component: createRouteContent(StrategiesDashboardPage, {
    title: 'Strategies Dashboard - New Version',
    description: 'Weavr | Strategies Dashboard New Version'
  }),
  errorComponent: GenericErrorComponent
});
const straNewRoute = createRoute({
  path: '/sstra/new',
  getParentRoute: () => rootRoute,
  component: createRouteContent(CreateNewStraPage, {
    title: 'Create Strategy',
    description: 'Weavr | Create Strategy'
  }),
  errorComponent: GenericErrorComponent
});
const straViewRoute = createRoute({
  path: '/sstra/$slug',
  getParentRoute: () => rootRoute,
  component: createRouteContent(StrategyViewPage, {
    title: 'Strategy',
    description: 'Weavr | Strategy'
  }),
  errorComponent: GenericErrorComponent,
  validateSearch: zodValidator(straViewSearchSchema)
});
const oldHomeRoute = createRoute({
  path: '/home',
  getParentRoute: () => rootRoute,
  component: createRouteContent(HomePageOld2, {
    title: 'Home',
    description: 'Weavr | Home Page'
  }),
  errorComponent: GenericErrorComponent,
  staticData: { excludeSitemap: true },
  beforeLoad: createBeforeLoad('/home')
});
const updatesRoute = createRoute({
  path: '/updates',
  getParentRoute: () => rootRoute,
  component: createRouteContent(SocialFeedPage, {
    title: 'Updates',
    description: 'Weavr | Updates'
  }),
  errorComponent: GenericErrorComponent,
  validateSearch: zodValidator(socialFeedSearchSchema),
  staticData: { excludeSitemap: true },
  beforeLoad: createBeforeLoad('/updates')
});
const singleUpdateRoute = createRoute({
  path: '/updates/$updateId',
  getParentRoute: () => rootRoute,
  component: createRouteContent(SinglePostPage, {
    title: 'Post',
    description: 'Weavr | Post'
  }),
  errorComponent: GenericErrorComponent,
  validateSearch: zodValidator(singleUpdateSearchSchema),
  staticData: { excludeSitemap: true },
  beforeLoad: createBeforeLoad('/updates/$updateId')
});
const reviewPublishRoute = createRoute({
  path: '/updates/review',
  getParentRoute: () => rootRoute,
  component: createRouteContent(ReviewPublishPage, {
    title: 'Review Publish',
    description: 'Weavr | Review pending publish items'
  }),
  errorComponent: GenericErrorComponent,
  staticData: { excludeSitemap: true },
  beforeLoad: ({ context }) => {
    if (!context.globalSettings) return;
    const hasAccess =
      context.globalSettings?.isGlobalAdmin ||
      context.globalSettings?.isStrategyOwner ||
      context.globalSettings?.enabledFeatures.includes('DeliverableWorkflows');
    if (!hasAccess) {
      throw redirect({ to: '/not-authorized' });
    }
  }
});
const strategiesRoute = createRoute({
  path: '/strategies',
  getParentRoute: () => rootRoute,
  errorComponent: GenericErrorComponent,
  validateSearch: zodValidator(strategiesSearchSchema),
  beforeLoad: ({ search }) => {
    const { strategyId, deliverableId } = search;

    if (strategyId) {
      throw redirect({
        to: '/sstra/$slug',
        params: { slug: `legacy_${strategyId}` },
        search: deliverableId ? { deliverableId } : {}
      });
    }

    throw redirect({ to: '/sstra' });
  }
});

const collaborateRoute = createRoute({
  path: '/collaborate',
  getParentRoute: () => rootRoute,
  component: createRouteContent(CollaboratePage, {
    title: 'Collaborate',
    description: 'Weavr | Collaborate'
  }),
  errorComponent: GenericErrorComponent,
  validateSearch: zodValidator(progressUpdateSearchSchema),
  beforeLoad: createBeforeLoad('/collaborate')
});
const ourJourneyRoute = createRoute({
  path: '/report/our_journey',
  getParentRoute: () => rootRoute,
  component: createRouteContent(OurJourney, {
    title: 'Insights dashboard',
    description: 'weavr | Insights dashboard'
  }),
  errorComponent: GenericErrorComponent,
  validateSearch: zodValidator(ourJourneySearchSchema),
  staticData: { excludeSitemap: true },
  beforeLoad: createBeforeLoad('/report/our_journey')
});
const ourCalendarRoute = createRoute({
  path: '/report/our_calendar',
  getParentRoute: () => rootRoute,
  component: createRouteContent(OurCalendar, {
    title: 'Our Calendar',
    description: 'Weaver | Our Calendar Report'
  }),
  errorComponent: GenericErrorComponent,
  validateSearch: zodValidator(ourCalendarSearchSchema),
  staticData: { excludeSitemap: true },
  beforeLoad: createBeforeLoad('/report/our_calendar')
});
const ourUpdateRoute = createRoute({
  path: '/report/our_updates',
  getParentRoute: () => rootRoute,
  component: createRouteContent(OurUpdates, {
    title: 'Our Updates',
    description: 'Weaver | Our Updates Report'
  }),
  errorComponent: GenericErrorComponent,
  staticData: { excludeSitemap: true },
  validateSearch: zodValidator(engagementSearchSchema),
  beforeLoad: createBeforeLoad('/report/our_updates')
});
const ourSonglineRoute = createRoute({
  path: '/report/our_songline',
  getParentRoute: () => rootRoute,
  validateSearch: zodValidator(songlineSearchSchema),
  component: createRouteContent(Songline, {
    title: 'Songline',
    description: 'Weaver | Our Songline Report'
  }),
  errorComponent: GenericErrorComponent,
  staticData: { excludeSitemap: true },
  beforeLoad: createBeforeLoad('/report/our_songline')
});
const photoAlbumRoute = createRoute({
  path: '/report/photo_album',
  getParentRoute: () => rootRoute,
  validateSearch: zodValidator(songlineSearchSchema),
  component: createRouteContent(PhotoAlbum, {
    title: 'Photo Album',
    description: 'Weaver | Photo Album'
  }),
  errorComponent: GenericErrorComponent,
  staticData: { excludeSitemap: true },
  beforeLoad: createBeforeLoad('/report/our_songline')
});
const collaborateEditRoute = createRoute({
  path: '/collaborate/edit',
  getParentRoute: () => rootRoute,
  component: createRouteContent(ProgressUpdatePage, {
    title: 'Progress update Edit',
    description: 'Weaver | Progress update'
  }),
  errorComponent: GenericErrorComponent,
  beforeLoad: createBeforeLoad('/collaborate/edit'),
  validateSearch: zodValidator(collaborateEditSearchSchema)
});
const hangfireRoute = createRoute({
  path: '/admin/hangfire',
  getParentRoute: () => rootRoute,
  component: createRouteContent(Hangfire, {
    title: 'Hangfire Dashboard',
    description: 'Hangfire Dashboard'
  }),
  errorComponent: GenericErrorComponent,
  beforeLoad: createBeforeLoad('/admin/hangfire')
});
const businessUnitRoute = createRoute({
  path: '/admin/business_units',
  getParentRoute: () => rootRoute,
  component: createRouteContent(BusinessUnits, {
    title: 'Admin - Weavr',
    description: 'Weavr | Business units'
  }),
  errorComponent: GenericErrorComponent,
  beforeLoad: createBeforeLoad('/admin/business_units')
});
const subscriptionRoute = createRoute({
  path: '/admin/subscription',
  getParentRoute: () => rootRoute,
  component: createRouteContent(SubscriptionEditPage, {
    title: 'Subscription',
    description: 'Weavr | Subscription'
  }),
  errorComponent: GenericErrorComponent,
  validateSearch: zodValidator(subscriptionSearchSchema),
  beforeLoad: createBeforeLoad('/admin/subscription')
});
const subscriptionListRoute = createRoute({
  path: globalAdminRoutes.allSubscriptions,
  getParentRoute: () => rootRoute,
  component: createRouteContent(Subscriptions, {
    title: 'Subscriptions',
    description: 'Weavr | global admin - subscriptions'
  }),
  errorComponent: GenericErrorComponent,
  beforeLoad: createBeforeLoad(globalAdminRoutes.allSubscriptions)
});
const globalSubscriptionRoute = createRoute({
  path: globalAdminRoutes.allSubscriptionsEdit,
  getParentRoute: () => rootRoute,
  component: createRouteContent(SubscriptionEditPage, {
    title: 'Subscription',
    description: 'Weavr | global admin - subscription'
  }),
  errorComponent: GenericErrorComponent,
  validateSearch: zodValidator(subscriptionSearchSchema),
  beforeLoad: createBeforeLoad(globalAdminRoutes.allSubscriptionsEdit)
});
const adminUserListRoute = createRoute({
  path: globalAdminRoutes.allUsers,
  getParentRoute: () => rootRoute,
  component: createRouteContent(UsersPage, {
    title: 'All Users',
    description: 'Weavr | global admin - All Users'
  }),
  errorComponent: GenericErrorComponent,
  beforeLoad: createBeforeLoad(globalAdminRoutes.allUsers)
});
const lookupRoute = createRoute({
  path: globalAdminRoutes.appLookups,
  getParentRoute: () => rootRoute,
  component: createRouteContent(AppLookups, {
    title: 'App Lookups',
    description: 'Weavr | App lookup'
  }),
  errorComponent: GenericErrorComponent,
  beforeLoad: createBeforeLoad(globalAdminRoutes.appLookups)
});
const moduleRoute = createRoute({
  path: globalAdminRoutes.appModules,
  getParentRoute: () => rootRoute,
  component: createRouteContent(AppModules, {
    title: 'App Modules',
    description: 'Weavr | App modules'
  }),
  errorComponent: GenericErrorComponent,
  beforeLoad: createBeforeLoad(globalAdminRoutes.appModules)
});
const roleRoute = createRoute({
  path: '/global_admin/roles',
  getParentRoute: () => rootRoute,
  component: createRouteContent(Roles, {
    title: 'Roles',
    description: 'Weavr | global admin - roles'
  }),
  errorComponent: GenericErrorComponent,
  beforeLoad: createBeforeLoad('/global_admin/roles')
});

const schedulerRoute = createRoute({
  path: globalAdminRoutes.scheduler,
  getParentRoute: () => rootRoute,
  component: createRouteContent(Scheduler, {
    title: 'Scheduler',
    description: 'Weavr | global admin - Scheduler'
  }),
  errorComponent: GenericErrorComponent,
  beforeLoad: createBeforeLoad(globalAdminRoutes.scheduler)
});
const contentBuilderRoute = createRoute({
  path: globalAdminRoutes.contentBuilder,
  getParentRoute: () => rootRoute,
  component: createRouteContent(ContentBuilder, {
    title: 'Content Builder',
    description: 'Weavr | Content builder'
  }),
  errorComponent: GenericErrorComponent,
  beforeLoad: createBeforeLoad(globalAdminRoutes.contentBuilder)
});
const systemRoute = createRoute({
  path: globalAdminRoutes.system,
  getParentRoute: () => rootRoute,
  component: createRouteContent(System, {
    title: 'System Info',
    description: 'Weavr | System info'
  }),
  errorComponent: GenericErrorComponent,
  beforeLoad: createBeforeLoad(globalAdminRoutes.system)
});
const profileRoute = createRoute({
  path: '/profile',
  getParentRoute: () => rootRoute,
  component: createRouteContent(Profile, {
    title: 'Profile',
    description: 'Weavr | Profile'
  }),
  errorComponent: GenericErrorComponent,
  staticData: { excludeSitemap: true },
  beforeLoad: createBeforeLoad('/profile')
});
// const careerRoute = createRoute({
//   path: '/careers',
//   getParentRoute: () => rootRoute,
//   component: createRouteContent(Vacancies, {
//     title: 'Careers',
//     description: 'Weavr | Careers',
//   }),
//   errorComponent: GenericErrorComponent,
//   beforeLoad: createBeforeLoad('/careers'),
// });
// const careerContentRoute = createRoute({
//   path: '/careers/$name',
//   getParentRoute: () => rootRoute,
//   component: createRouteContent(VacancyPage, {
//     title: 'Careers',
//     description: 'Weavr | Careers',
//   }),
//   errorComponent: GenericErrorComponent,
//   validateSearch: zodValidator(vacancySearchSchema),
//   beforeLoad: createBeforeLoad('/careers/$name'),
// });
// const vacancyRoute = createRoute({
//   path: '/global_admin/candidate_applications',
//   getParentRoute: () => rootRoute,
//   component: createRouteContent(VacancyApplicationsList, {
//     title: 'Application Management',
//     description: 'Weavr | Manage Candidate Applications',
//   }),
//   errorComponent: GenericErrorComponent,
//   beforeLoad: createBeforeLoad('/global_admin/candidate_applications'),
// });
const blogRoute = createRoute({
  path: '/blogs',
  getParentRoute: () => rootRoute,
  component: createRouteContent(BlogsLanding, {
    title: 'Blog',
    description: 'Weavr | Blogs'
  }),
  errorComponent: GenericErrorComponent,
  beforeLoad: createBeforeLoad('/blogs')
});
const blogContentRoute = createRoute({
  path: '/blogs/$name',
  getParentRoute: () => rootRoute,
  component: createRouteContent(Blogs, {
    title: 'Blog',
    description: 'Weavr | Blogs'
  }),
  errorComponent: GenericErrorComponent,
  beforeLoad: createBeforeLoad('/blogs/:name')
});

const caseStudiesRoute = createRoute({
  path: '/casestudies',
  getParentRoute: () => rootRoute,
  component: createRouteContent(CaseStudiesLanding, {
    title: 'Case Studies',
    description: 'Weavr | Case Studies'
  }),
  errorComponent: GenericErrorComponent,
  beforeLoad: createBeforeLoad('/casestudies')
});
const caseStudyContentRoute = createRoute({
  path: '/casestudies/$name',
  getParentRoute: () => rootRoute,
  component: createRouteContent(CaseStudyPage, {
    title: 'Case Studies',
    description: 'Weavr | Case Studies'
  }),
  errorComponent: GenericErrorComponent,
  beforeLoad: createBeforeLoad('/casestudies/:name')
});

const knowledgeBaseList = createRoute({
  path: '/knowledgebase',
  getParentRoute: () => rootRoute,
  component: createRouteContent(KnowledgeBaseLanding, {
    title: 'Knowledge Base',
    description: 'Weavr | Knowledge Base'
  }),
  errorComponent: GenericErrorComponent,
  validateSearch: zodValidator(knowledgebaseSearchSchema),
  beforeLoad: createBeforeLoad('/knowledgebase')
});
const knowledgeBase = createRoute({
  path: '/knowledgebase/$title',
  getParentRoute: () => rootRoute,
  component: createRouteContent(KnowledgeBasePage, {
    title: 'Knowledge Base',
    description: 'Weavr | Blogs'
  }),
  errorComponent: GenericErrorComponent,
  beforeLoad: createBeforeLoad('/knowledgebase/$title')
});
const notAuthRoute = createRoute({
  path: '/not-authorized',
  getParentRoute: () => rootRoute,
  component: createRouteContent(NotAuthorized, {
    title: 'Not Authorized',
    description: 'Weavr | Not Authorized to view this page'
  }),
  errorComponent: GenericErrorComponent,
  staticData: { excludeSitemap: true }
});
const notFoundRoute = createRoute({
  path: '*',
  getParentRoute: () => rootRoute,
  component: createRouteContent(NotFound, {
    title: '404 Not Found',
    description: 'Weavr | Page not found'
  }),
  errorComponent: GenericErrorComponent,
  staticData: { excludeSitemap: true }
});

export const routeTree = rootRoute.addChildren([
  homeRoute,
  straRoute,
  straNewRoute,
  straViewRoute,
  termsConditionRoute,
  securityRoute,
  privacyRoute,
  ourUseOfAIRoute,
  oldHomeRoute,
  updatesRoute,
  singleUpdateRoute,
  reviewPublishRoute,
  strategiesRoute,
  collaborateRoute,
  collaborateEditRoute,
  ourJourneyRoute,
  ourCalendarRoute,
  ourUpdateRoute,
  ourSonglineRoute,
  photoAlbumRoute,
  hangfireRoute,
  businessUnitRoute,
  adminUserListRoute,
  subscriptionRoute,
  lookupRoute,
  moduleRoute,
  roleRoute,
  subscriptionListRoute,
  globalSubscriptionRoute,
  schedulerRoute,
  systemRoute,
  profileRoute,
  contentBuilderRoute,
  // careerRoute,
  // careerContentRoute,
  // vacancyRoute,
  blogRoute,
  blogContentRoute,
  caseStudiesRoute,
  caseStudyContentRoute,
  knowledgeBaseList,
  knowledgeBase,
  notAuthRoute,
  notFoundRoute
]);
